/**
 * Pure model turning a gate `402` into a paywall prompt — the client half of the
 * "quota/credit gate IS the paywall funnel" (memory `monetization-onboarding-funnel`,
 * billing.md § "The gate"). The server is the sole authority on the limit; this
 * only READS the error the edge already returned (`quota_exceeded` /
 * `daily_cap_exceeded`, each carrying an `upsell` hint) and shapes it into what
 * the paywall renders. JSX-free + DOM-free so `bun test` exercises it directly.
 *
 * No client-side cap arithmetic lives here: the free-chat cap + pay-to-generate
 * thresholds are per-tier config on the edge (`apps/backend/src/billing/`), never
 * duplicated on the client — a `402` is the only trigger.
 */
import type { ApiError } from '@4f/shared';

/** The gate `402` error codes that open the paywall (billing.md § "The gate"). */
const PAYWALL_CODES = new Set(['quota_exceeded', 'daily_cap_exceeded']);

/** The upsell actions a paywall may offer — mirrors the gate's `details.upsell`. */
export interface PaywallUpsell {
  readonly topUp: boolean;
  readonly upgrade: boolean;
}

/** A resolved paywall prompt derived from a gate `402`. */
export interface PaywallPrompt {
  /** Which ceiling tripped — the free daily-chat cap, or an empty credit balance. */
  readonly reason: 'daily_cap' | 'credits';
  /** i18n key for the headline. */
  readonly titleKey: string;
  /** i18n key for the explanation. */
  readonly bodyKey: string;
  /** Which upsell CTAs to show (a daily-cap lift is an upgrade, not a top-up). */
  readonly upsell: PaywallUpsell;
}

/**
 * Read the gate's `details.upsell` (best-effort) — an action is shown unless the
 * edge explicitly set it `false`, so a malformed/absent hint degrades to offering
 * both rather than a dead-end paywall.
 */
function readUpsell(error: ApiError): PaywallUpsell {
  const raw = error.details?.upsell;
  if (raw && typeof raw === 'object') {
    const hint = raw as Record<string, unknown>;
    return { topUp: hint.topUp !== false, upgrade: hint.upgrade !== false };
  }
  return { topUp: true, upgrade: true };
}

/**
 * Derive a {@link PaywallPrompt} from an api error, or `null` when the error is
 * not a gate `402` (a network / validation / auth error renders as a plain error,
 * never the paywall). The daily-cap `402` is lifted by UPGRADING (not a top-up),
 * so its copy + upsell point at the plan; the credit `402` offers both doors.
 */
export function paywallFromError(error: ApiError | null): PaywallPrompt | null {
  if (!error) {
    return null;
  }
  return paywallFromCode(error.code, readUpsell(error));
}

/**
 * The same decision from a bare error CODE, for the gate `402` that arrives as a
 * `run-error` frame mid-stream rather than as an `ApiError` before the stream
 * opens.
 *
 * That case is the expensive one — the credits ran out DURING a generation — and
 * it rendered "Something went wrong / Retry", whose retry re-spends. It has to
 * reach the same paywall the pre-stream `402` reaches; routing both through one
 * function is what guarantees the two can never drift.
 *
 * A frame carries no `details.upsell`, so `upsell` defaults to offering both
 * doors — the same degradation {@link readUpsell} applies to a malformed hint,
 * and the daily-cap branch below still forces upgrade-only regardless.
 */
export function paywallFromCode(
  // Both absent shapes are accepted because both exist: the wire frame's `code`
  // is optional, and `StreamState.errorCode` stores its absence as `null`.
  code: string | null | undefined,
  upsell: PaywallUpsell = { topUp: true, upgrade: true },
): PaywallPrompt | null {
  if (!code || !PAYWALL_CODES.has(code)) {
    return null;
  }
  if (code === 'daily_cap_exceeded') {
    return {
      reason: 'daily_cap',
      titleKey: 'chat.paywall.dailyCap.title',
      bodyKey: 'chat.paywall.dailyCap.body',
      // A per-day cap is lifted ONLY by upgrading — credits can't raise it — so
      // the daily-cap prompt is upgrade-only. Never forward `topUp` here even if a
      // malformed hint set it, keeping the funnel contract (billing.md § "The gate").
      upsell: { topUp: false, upgrade: upsell.upgrade },
    };
  }
  return {
    reason: 'credits',
    titleKey: 'chat.paywall.credits.title',
    bodyKey: 'chat.paywall.credits.body',
    upsell,
  };
}
